import React from 'react';
import { Upload, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import Papa from 'papaparse';
import { useUploadContextFactory } from './upload-contexts/use-upload-context-factory';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import styles from './csv-file-upload.module.scss';

const { Dragger } = Upload;

const CSVFileUpload = ({ contextType }) => {
  const lang = useSelector(getLang('SETTINGS'));
  const { handleFileUpload, CustomFileUploadRender = null } = useUploadContextFactory({
    contextType,
  });

  const handleFileInputChange = file => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      trimHeaders: true,
      complete: results => {
        const { data, errors } = results;

        // Check for parsing errors
        if (errors && errors.length > 0) {
          console.error('CSV parsing errors:', errors);
          const criticalErrors = errors.filter(
            err => err.type === 'Quotes' || err.type === 'FieldMismatch',
          );
          if (criticalErrors.length > 0) {
            message.error(lang.CSV_PARSING_ERROR.replace('{message}', criticalErrors[0].message));
            return;
          }
        }

        // Validate data
        if (!data || data.length === 0) {
          message.error(lang.CSV_EMPTY);
          return;
        }

        if (data.length < 2) {
          message.error(lang.CSV_NO_DATA);
          return;
        }

        // Extract headers and data rows
        const rawHeaders = data[0].map(header => (header || '').trim());
        const rawDataRows = data.slice(1).map(row => row.map(cell => (cell || '').trim()));

        // Identify non-empty columns (columns with header OR any non-empty value)
        const nonEmptyColumnIndices = rawHeaders
          .map((header, index) => {
            const hasHeader = header && header.trim() !== '';
            const hasData = rawDataRows.some(row => row[index] && row[index].trim() !== '');
            return hasHeader || hasData ? index : -1;
          })
          .filter(index => index !== -1);

        // Filter out empty columns
        const headers = nonEmptyColumnIndices.map(index => rawHeaders[index]);
        const dataRows = rawDataRows.map(row =>
          nonEmptyColumnIndices.map(index => row[index] || ''),
        );

        // Validate headers
        if (headers.length === 0) {
          message.error(lang.CSV_NO_COLUMNS);
          return;
        }

        // Validate data rows
        const validDataRows = dataRows.filter(row => row.some(cell => cell));
        if (validDataRows.length === 0) {
          message.error(lang.CSV_NO_ROWS);
          return;
        }

        handleFileUpload({ headers, rows: validDataRows });
      },
      error: error => {
        console.error('Papa Parse error:', error);
        message.error(lang.CSV_PARSE_FAILED.replace('{message}', error.message));
      },
    });

    return false; // Prevent default upload behavior
  };

  return (
    <div>
      {CustomFileUploadRender && <CustomFileUploadRender />}
      <Dragger
        name="file"
        multiple={false}
        accept=".csv"
        beforeUpload={handleFileInputChange}
        showUploadList={false}
        rootClassName={styles.dragger}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{lang.CSV_UPLOAD_TITLE}</p>
        <p className="ant-upload-hint">{lang.CSV_UPLOAD_HINT}</p>
      </Dragger>
    </div>
  );
};

export default CSVFileUpload;
